import { Button, Card, ConfirmModal, Panel, Table } from '@/components/Bicester';
import Image from '@/components/Bicester/Image';
import { Form } from '@/components/formik-rsuite';
import { useRequest } from 'ahooks';
import { FormikProvider, useFormik } from 'formik';
import React, { useState, useEffect } from 'react';
import { IconButton, Alert } from 'rsuite';
import { DailyService, defaultRecordInfo, SiteDetailPage, SubscribeDetailPage } from './constants';
import useTable from '@/hooks/useTable';
import { Cell, DateTimeCell, UserNameCell } from '@/components/Bicester/Table/Cells';
import { getPathname, push } from '@/utils/history';
import StatusCell from '@/components/Bicester/Table/Cells/StatusCell';
import { BICESTER_FULL_DATE_HOURS_MINUTES, FULL_DATE } from '@/constants/dateFormat';
import useContentHeight from '@/hooks/useContentHeight';
import { useReadyInfo } from '@/providers/ReadyProvider';
import { Bicester, SERVICE_STATUS_TYPE } from '@/constants';
import { fetchServiceRecord } from '@/services/site/record';
import PageLoader from '@/components/shared/PageLoader';
import useLoading from '@/hooks/useLoading';
import _ from 'lodash';
import dayjs from 'dayjs';
import { useModal } from '@ebay/nice-modal-react';
import { fetchCancelService } from '@/services/site/lease';
import useServiceBasic from '@/hooks/preload/useServiceBasic';
import { getDetailPath } from '@/views/SubscribeService/constants';
import { SERVICE_STATUS } from '@/services/constants';
import { LAYOUT_CONFIG } from '@/constants/layoutConfig';
import { PopoverWrapper } from '@/components/Bicester/PopoverWrapper';

const SiteServiceRecordList = () => {
  const { profile } = useReadyInfo();
  const { data: serviceBasic, loading: serviceBasicLoading } = useServiceBasic();
  const modal = useModal(ConfirmModal);

  const {
    run: getServiceRecord,
    data: serviceRecordData = {},
    loading: serviceRecordLoading,
  } = useRequest(fetchServiceRecord, {
    manual: true,
  });
  const { run: cancelService, loading: cancelServiceLoading } = useRequest(fetchCancelService, {
    manual: true,
  });

  const [defaultInfo, setDefaultInfo] = useState(defaultRecordInfo);
  const [currentVillage, setCurrentVillage] = useState(profile?.shopping_village);

  const { onChange, onSearch, onRefresh } = useTable(
    async (p) => {
      await getServiceRecord(p).then((res) => setCurrentVillage(formik.values?.shopping_village));
    },
    {
      start_at: defaultInfo.time_range[0],
      end_at: defaultInfo.time_range[1],
    }
  );

  useEffect(() => {
    setDefaultInfo({
      ...defaultInfo,
      shopping_village: profile?.shopping_village,
    });
    getServiceRecord(
      Object.assign({}, _.omit(defaultInfo, 'time_range'), {
        start_at: defaultInfo.time_range[0],
        end_at: defaultInfo.time_range[1],
        shopping_village: profile?.shopping_village,
      })
    );
  }, []);

  const formik = useFormik({
    initialValues: defaultInfo,
    enableReinitialize: true, // initialValues默认值受控
    onSubmit(values, actions) {
      const params = Object.assign({}, _.omit(values, 'time_range'), {
        start_at:
          (values?.time_range && values?.time_range?.length === 2 && values?.time_range[0]) || '',
        end_at:
          (values?.time_range && values?.time_range?.length === 2 && values?.time_range[1]) || '',
        type: values?.type?.length ? values?.type?.join() : '',
      });
      onSearch(params);
    },
  });

  const loading = useLoading(serviceBasicLoading);

  return (
    <>
      <Panel title="查询条件" dividerHeight={15} leftShadow className="py-[25px]">
        {loading && <PageLoader />}
        <FormikProvider value={formik}>
          <Form>
            <Card smDivider>
              <div className="flex items-center">
                <Card.Picker
                  label="购物村"
                  formKey="shopping_village"
                  pickerType="village"
                  cleanable={false}
                />
                <Card.Input label="手机号" formKey="phone" />
                <Card.Input label="会员号" formKey="user_code" />
                <Card.Input
                  label="关键词"
                  formKey="content"
                  hasBtn
                  customBtn={
                    <IconButton type="submit" className="flex items-center w-[30px] ml-[10px]">
                      <Image src="/resources/images/match.svg" />
                    </IconButton>
                  }
                />
              </div>
              <div className="flex">
                <Card.Picker
                  label="服务状态"
                  formKey="status"
                  pickerData={serviceBasic?.service_status}
                />
                <Card.CheckPicker
                  label="服务项目"
                  formKey="type"
                  pickerData={serviceBasic?.service_type}
                />
                <Card.DateRangePicker label="服务时间" formKey="time_range" />
                <Card.Input label="服务编号" formKey="service_no" />
              </div>
            </Card>
          </Form>
        </FormikProvider>
      </Panel>
      <Panel
        title={`${Bicester[currentVillage]}服务记录`}
        loading={cancelServiceLoading}
        dividerHeight={15}
        leftShadow
        className="py-[25px]"
        style={{
          height:
            'max(calc(100vh - var(--headerHeight) - var(--navBarHeight) - var(--toolBarHeight) - 199px), auto)',
        }}
        subTitle={`总计 ${serviceRecordData?.total || 0} 条`}
      >
        <Table
          style={{
            height: `max(${
              useContentHeight({
                withToolBar: true,
                withPanelPaddingTop: true,
                withPanelPaddingBottom: true,
                withPanelHeader: true,
                withTablePagination: true,
              }) +
              (22 - 199 + 21)
            }px, ${
              LAYOUT_CONFIG.TABLE_MIN_ROWS_WITH_HEADER_HEIGHT +
              LAYOUT_CONFIG.TABLE_PAGINATION_WITH_MARGIN_HEIGHT
            }px})`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
          loading={serviceRecordLoading}
          pageInfo={{ page: serviceRecordData?.current_page, total: serviceRecordData?.total }}
          data={serviceRecordData?.content_list ?? []}
          columns={[
            {
              columnProps: {
                flexGrow: 1.8,
              },
              header: '服务编号',
              cell: <Cell dataKey="service_no" />,
            },
            {
              columnProps: {
                flexGrow: 1,
              },
              header: '客户名',
              cell: <UserNameCell dataKey="user_name" />,
            },
            {
              columnProps: {
                flexGrow: 1.5,
              },
              header: '手机号',
              cell: <Cell dataKey="phone" />,
            },
            {
              columnProps: {
                flexGrow: 1.7,
              },
              header: '开始时间',
              cell: <DateTimeCell dataKey="start_time" format={BICESTER_FULL_DATE_HOURS_MINUTES} />,
            },
            {
              columnProps: {
                flexGrow: 1.2,
              },
              header: '服务项目',
              cell: <Cell dataKey="type_name" />,
            },
            {
              columnProps: {
                flexGrow: 2.2,
              },
              header: '服务详情',
              cell: (
                <Cell dataKey="service_content">
                  {(rowData) => (
                    <PopoverWrapper popover={{ content: rowData?.service_content }}>
                      <div className="rs-table-cell-content px-0">{rowData?.service_content}</div>
                    </PopoverWrapper>
                  )}
                </Cell>
              ),
            },
            {
              columnProps: {
                flexGrow: 1,
              },
              header: '服务状态',
              cell: <StatusCell dataKey="status" statusOptionsData={serviceBasic.service_status} />,
            },
            {
              columnProps: {
                flexGrow: 1,
              },
              header: '记录详情',
              cell: (
                <Cell>
                  {(rowData) => {
                    const { id, type, status, created_at, type_name } = rowData;
                    return (
                      <>
                        <Button
                          appearance="link"
                          onClick={() => {
                            if (
                              Object.keys(SiteDetailPage)
                                .map((key) => Number(key))
                                .includes(type)
                            ) {
                              window.open(
                                getPathname(
                                  `/site-service/record/info/${SiteDetailPage[type]}/${id}`
                                )
                              );
                            } else if (
                              Object.keys(SubscribeDetailPage)
                                .map((key) => Number(key))
                                .includes(type)
                            ) {
                              window.open(
                                getPathname(`${getDetailPath(SubscribeDetailPage[type])}/${id}`)
                              );
                            }
                          }}
                        >
                          查看
                        </Button>
                        {!(
                          status === SERVICE_STATUS_TYPE.CANCELED ||
                          status === SERVICE_STATUS_TYPE.EXPIRED ||
                          // 服务记录列表中非日常服务的已完成服务不可取消
                          (status === SERVICE_STATUS_TYPE.FINISHED &&
                            !Object.keys(DailyService)
                              .map((key) => Number(key))
                              .includes(type))
                        ) && (
                          <Button
                            style={{ marginLeft: 10 }}
                            appearance="link"
                            onClick={() => {
                              modal.show({
                                contentText: (
                                  <>
                                    <div>
                                      {`${dayjs(created_at).format(
                                        BICESTER_FULL_DATE_HOURS_MINUTES
                                      )} ${type_name}`}
                                    </div>
                                    <div>是否取消本次服务？取消后无法恢复</div>
                                  </>
                                ),
                                submitText: '是',
                                cancelText: '否',
                                onSubmit: () => {
                                  cancelService(id).then((res) => {
                                    if (res?.status === SERVICE_STATUS.SUCCESS) {
                                      Alert.success('取消成功');
                                      onRefresh();
                                    }
                                  });
                                },
                              });
                            }}
                          >
                            取消
                          </Button>
                        )}
                      </>
                    );
                  }}
                </Cell>
              ),
            },
          ]}
          onChange={onChange}
        >
          <Table.Body
            height={Math.max(
              useContentHeight({
                withToolBar: true,
                withPanelPaddingTop: true,
                withPanelPaddingBottom: true,
                withPanelHeader: true,
                withTablePagination: true,
              }) +
                (22 - 199 + 21),
              LAYOUT_CONFIG.TABLE_MIN_ROWS_WITH_HEADER_HEIGHT
            )}
          />
          <Table.Pagination />
        </Table>
      </Panel>
    </>
  );
};

export default SiteServiceRecordList;
